import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "@/lib/auth";
import ProtectedRoute from "@/components/ProtectedRoute";
import Login from "@/pages/Login";
import Register from "@/pages/Register";
import Dashboard from "@/pages/Dashboard";
import Invoices from "@/pages/Invoices";
import InvoiceDetail from "@/pages/InvoiceDetail";
import Connections from "@/pages/Connections";
import Activity from "@/pages/Activity";
import Mappings from "@/pages/Mappings";
import Vendors from "@/pages/Vendors";
import Customers from "@/pages/Customers";
import Settings from "@/pages/Settings";
import Terms from "@/pages/Terms";
import Privacy from "@/pages/Privacy";
import Contact from "@/pages/Contact";
import Help from "@/pages/Help";
import Home from "@/pages/Home";
import BillingEmailConfirm from "@/pages/BillingEmailConfirm";
import QboDisconnected from "@/pages/QboDisconnected";
import { QboLaunch, QboConnect } from "@/pages/QboEntry";
import Diagnostics from "@/pages/Diagnostics";
import AdminUsage from "@/pages/AdminUsage";

// Logged-out visitors get the public homepage; signed-in users get the dashboard.
function HomeRoute() {
  const { user } = useAuth();
  if (user === false) return <Home />;
  return <ProtectedRoute><Dashboard /></ProtectedRoute>;
}

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <Routes>
          <Route path="/login" element={<Login />} />
          <Route path="/register" element={<Register />} />
          {/* Prompt 16 — public legal pages, no auth guard, no redirect. */}
          <Route path="/terms" element={<Terms />} />
          <Route path="/privacy" element={<Privacy />} />
          <Route path="/contact" element={<Contact />} />
          <Route path="/help" element={<Help />} />
          {/* Prompt 17 — public billing-email confirmation page. */}
          <Route path="/billing-emails/confirm" element={<BillingEmailConfirm />} />
          {/* Prompt 18 A3 — public QuickBooks "disconnected" page (no auth, no API). */}
          <Route path="/qbo/disconnected" element={<QboDisconnected />} />
          {/* Prompt 18 A3 — Intuit Launch URL target. Routes based on session. */}
          <Route path="/qbo/launch" element={<QboLaunch />} />
          {/* Prompt 18 A3 — Intuit Connect/Reconnect URL target. Requires login. */}
          <Route path="/qbo/connect" element={<QboConnect />} />
          <Route
            path="/"
            element={<HomeRoute />}
          />
          <Route
            path="/invoices"
            element={
              <ProtectedRoute>
                <Invoices />
              </ProtectedRoute>
            }
          />
          <Route
            path="/invoices/:id"
            element={
              <ProtectedRoute>
                <InvoiceDetail />
              </ProtectedRoute>
            }
          />
          <Route
            path="/connections"
            element={
              <ProtectedRoute>
                <Connections />
              </ProtectedRoute>
            }
          />
          <Route
            path="/activity"
            element={
              <ProtectedRoute>
                <Activity />
              </ProtectedRoute>
            }
          />
          <Route
            path="/mappings"
            element={
              <ProtectedRoute>
                <Mappings />
              </ProtectedRoute>
            }
          />
          <Route
            path="/vendors"
            element={
              <ProtectedRoute>
                <Vendors />
              </ProtectedRoute>
            }
          />
          <Route
            path="/customers"
            element={
              <ProtectedRoute>
                <Customers />
              </ProtectedRoute>
            }
          />
          <Route
            path="/settings"
            element={
              <ProtectedRoute>
                <Settings />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/usage"
            element={
              <ProtectedRoute>
                <AdminUsage />
              </ProtectedRoute>
            }
          />
          {/* Prompt 18 A1/A2/A8/B5 — admin-only diagnostics. API 403s non-admins. */}
          <Route
            path="/diagnostics"
            element={
              <ProtectedRoute>
                <Diagnostics />
              </ProtectedRoute>
            }
          />
          {/* Legacy alias — keeps any old bookmarks / deep links working. */}
          <Route path="/dashboard" element={<Navigate to="/" replace />} />
          <Route path="/needs-me" element={<Navigate to="/vendors" replace />} />
          <Route path="/billing-emails" element={<Navigate to="/settings" replace />} />
        </Routes>
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
